"use client"

import * as React from "react"
import Link from "next/link"
import { motion, AnimatePresence } from "framer-motion"
import {
    User,
    LayoutDashboard,
    CreditCard,
    Swords,
    Users,
    Shield,
    Settings,
    Crown,
    LogOut,
    ChevronDown,
    Flame,
    Flag,
    Gift
} from "lucide-react"
import { signOut } from "next-auth/react"
import { cn } from "@/lib/utils"

interface UserNavDropdownProps {
    user: {
        name?: string | null
        email?: string | null
        login?: string | null
        isAdmin?: boolean
    }
}

export function UserNavDropdown({ user }: UserNavDropdownProps) {
    const [isOpen, setIsOpen] = React.useState(false)
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)

    const handleMouseEnter = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
        setIsOpen(true)
    }

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setIsOpen(false)
        }, 180)
    }

    React.useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
        }
    }, [])

    const displayName = user.login || user.name || "Hero"
    const isAdmin = Boolean(user.isAdmin)

    return (
        <div
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {/* Pill Trigger */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    "flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full border transition-all text-xs font-bold cursor-pointer font-sans backdrop-blur-md",
                    isOpen
                        ? "bg-slate-900 border-amber-500/40 text-amber-300 shadow-lg shadow-amber-500/10"
                        : "bg-slate-900/70 hover:bg-slate-900 border-slate-800 text-slate-300 hover:text-white"
                )}
            >
                <div className="w-5 h-5 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
                    {isAdmin ? <Crown className="w-3 h-3" /> : <User className="w-3 h-3" />}
                </div>

                <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-slate-400 font-normal hidden xl:inline">Hero:</span>
                    <span className="text-[11px] font-bold text-amber-400 font-cinzel truncate max-w-[110px]">
                        {displayName}
                    </span>
                </div>

                <ChevronDown className={cn(
                    "w-3 h-3 text-slate-500 transition-transform duration-200",
                    isOpen && "rotate-180 text-amber-400"
                )} />
            </button>

            {/* Account Dropdown Menu */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 6, scale: 0.98 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 top-full pt-2 z-50 w-64 max-w-[90vw]"
                    >
                        <div className="rounded-2xl bg-slate-950/95 backdrop-blur-2xl border border-slate-800/90 shadow-2xl shadow-black/90 p-3 overflow-hidden">
                            {/* Profile Header */}
                            <div className="p-2.5 rounded-xl bg-slate-900/80 border border-slate-800/80 mb-2">
                                <div className="flex items-center justify-between">
                                    <div className="min-w-0">
                                        <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                                            DragonL2 Account
                                        </div>
                                        <div className="text-xs font-bold text-white font-cinzel truncate">
                                            {displayName}
                                        </div>
                                    </div>
                                    <span className={cn(
                                        "text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded border tracking-wider",
                                        isAdmin
                                            ? "bg-amber-500/10 text-amber-400 border-amber-500/30"
                                            : "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                                    )}>
                                        {isAdmin ? "Staff Admin" : "Active Hero"}
                                    </span>
                                </div>

                                <Link
                                    href="/dashboard"
                                    onClick={() => setIsOpen(false)}
                                    className="mt-2.5 flex items-center justify-center gap-1.5 w-full py-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[10px] font-bold uppercase tracking-wider transition-all"
                                >
                                    <LayoutDashboard className="w-3 h-3" />
                                    <span>Command Dashboard</span>
                                </Link>
                            </div>

                            {/* Shortcuts List */}
                            <div className="space-y-0.5 py-1">
                                {isAdmin && (
                                    <Link
                                        href="/admin"
                                        onClick={() => setIsOpen(false)}
                                        className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-amber-500/10 text-amber-300 text-xs font-bold transition-all border border-transparent hover:border-amber-500/20"
                                    >
                                        <Crown className="w-3.5 h-3.5 text-amber-400" />
                                        <span>Master Admin Panel</span>
                                    </Link>
                                )}

                                <Link
                                    href="/dashboard/characters"
                                    onClick={() => setIsOpen(false)}
                                    className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-900 text-slate-300 hover:text-white text-xs font-medium transition-all"
                                >
                                    <Swords className="w-3.5 h-3.5 text-slate-400" />
                                    <span>My Characters</span>
                                </Link>

                                <Link
                                    href="/dashboard/accounts"
                                    onClick={() => setIsOpen(false)}
                                    className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-900 text-slate-300 hover:text-white text-xs font-medium transition-all"
                                >
                                    <Users className="w-3.5 h-3.5 text-slate-400" />
                                    <span>Game Accounts</span>
                                </Link>

                                <Link
                                    href="/dashboard/balance"
                                    onClick={() => setIsOpen(false)}
                                    className="flex items-center justify-between px-2.5 py-1.5 rounded-lg hover:bg-slate-900 text-slate-300 hover:text-white text-xs font-medium transition-all"
                                >
                                    <div className="flex items-center gap-2.5">
                                        <CreditCard className="w-3.5 h-3.5 text-slate-400" />
                                        <span>Top Up Balance</span>
                                    </div>
                                    <span className="text-[8px] font-extrabold uppercase px-1 py-0.2 rounded bg-rose-500/20 text-rose-300 border border-rose-500/30">
                                        +40%
                                    </span>
                                </Link>

                                <Link
                                    href="/dashboard/shop"
                                    onClick={() => setIsOpen(false)}
                                    className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-900 text-slate-300 hover:text-white text-xs font-medium transition-all"
                                >
                                    <Gift className="w-3.5 h-3.5 text-slate-400" />
                                    <span>Claim Starter Pack</span>
                                </Link>

                                <Link
                                    href="/dashboard/clan-migrate"
                                    onClick={() => setIsOpen(false)}
                                    className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-900 text-slate-300 hover:text-white text-xs font-medium transition-all"
                                >
                                    <Flag className="w-3.5 h-3.5 text-slate-400" />
                                    <span>Clan Migration</span>
                                </Link>

                                <Link
                                    href="/dashboard/security"
                                    onClick={() => setIsOpen(false)}
                                    className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-900 text-slate-300 hover:text-white text-xs font-medium transition-all"
                                >
                                    <Shield className="w-3.5 h-3.5 text-slate-400" />
                                    <span>Account Security</span>
                                </Link>

                                <Link
                                    href="/dashboard/settings"
                                    onClick={() => setIsOpen(false)}
                                    className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-900 text-slate-300 hover:text-white text-xs font-medium transition-all"
                                >
                                    <Settings className="w-3.5 h-3.5 text-slate-400" />
                                    <span>Account Settings</span>
                                </Link>
                            </div>

                            {/* Log Out Button */}
                            <div className="pt-2 border-t border-slate-900 mt-1">
                                <button
                                    type="button"
                                    onClick={() => signOut()}
                                    className="flex items-center gap-2.5 w-full px-2.5 py-1.5 rounded-lg hover:bg-red-500/10 text-slate-400 hover:text-red-400 text-xs font-medium transition-all cursor-pointer"
                                >
                                    <LogOut className="w-3.5 h-3.5" />
                                    <span>Sign Out of DragonL2</span>
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
